import React from 'react';
import {
	AppliedCorrectionOut,
	CitationOut,
	DocumentWithDownloadUrl as Document,
	OfferOutcome,
	PendingActionPromptOutput,
	PendingActionType,
	PendingOption,
	SourceOut,
	WebSourceOut,
} from '@/client';
import {
	ChatExpiredOfferNotice,
	ChatOfferOutcomeCard,
	ChatPendingActionCard,
	ChatResponseCorrectionsBadge,
	ChatResponseLoadingIndicator,
	ChatResponseSources,
	ChatResponseText,
	ChatResponseActions,
	ChatResponseWebSources,
} from './ChatResponse';
import { isResolvedOfferOutcome } from './ChatResponse/chatPendingActionCopy';
import type { EvidenceTab } from './ChatResponse/ChatResponseSources';
import { AssistantResponseData } from '@/types/Document.ts';
import { Box } from '@chakra-ui/react';

interface ChatContainerResponseProps {
	isLoading: boolean;
	responseText: string;
	chatMode: boolean;
	response: AssistantResponseData | null;
	documents: Document[];
	highlightedAnnotationIndex: number | null;
	parseResponseTextWithLinks: (text: string) => string;
	exchangeId: string | null;
	sources?: SourceOut[];
	citations?: CitationOut[];
	showCitationPills?: boolean;
	registerAnnotationRef: (index: number, element: HTMLElement | null) => void;
	onCitationClick: (sourceIndex: number) => void;
	onOpenDocument: (doc: Document, pages?: number[]) => void;
	sourcesOpen: boolean;
	onSourcesOpenChange: (open: boolean) => void;
	onSourcesAnimationStart: () => void;
	/** External pages cited on a web-fallback answer. */
	webSources?: WebSourceOut[];
	/** Saved corrections/facts that fed this answer. */
	appliedCorrections?: AppliedCorrectionOut[];
	/** Set when this turn ended by asking rather than answering — live or historical. */
	pendingAction?: PendingActionPromptOutput | null;
	/** Which kind of offer this turn made, whether or not it's still answerable — history-only. See
	 * AssistantQuery.offeredAction. */
	offeredAction?: PendingActionType | null;
	/** What became of the offer, once answered — history-only. See AssistantQuery.offerOutcome. */
	offerOutcome?: OfferOutcome | null;
	/** Live progress label while this turn is loading. See AssistantQuery.statusLabel. */
	statusLabel?: string | null;
	/** Sends the chosen option's reply as the next message. Required whenever `pendingAction` is set. */
	onSubmitPendingReply?: (option: PendingOption) => void;
}

/**
 * Container for the assistant response area including loading indicator,
 * Markdown-rendered response text, and annotations list.
 */
const ChatContainerResponse: React.FC<ChatContainerResponseProps> = (props: ChatContainerResponseProps) => {
	const [evidenceTab, setEvidenceTab] = React.useState<EvidenceTab>('records');
	const externalEvidenceTriggerRef = React.useRef<HTMLButtonElement | null>(null);
	const {
		isLoading,
		responseText,
		chatMode,
		documents,
		highlightedAnnotationIndex,
		parseResponseTextWithLinks,
		exchangeId,
		sources,
		citations,
		showCitationPills,
		registerAnnotationRef,
		onCitationClick,
		onOpenDocument,
		sourcesOpen,
		onSourcesOpenChange,
		onSourcesAnimationStart,
		webSources,
		appliedCorrections,
		pendingAction,
		offeredAction,
		offerOutcome,
		statusLabel,
		onSubmitPendingReply,
	} = props;

	// An outcome that doesn't render as its own state falls through to the plain-text branch below.
	const resolvedOutcome = isResolvedOfferOutcome(offerOutcome) ? offerOutcome : null;
	const openEvidence = (tab: EvidenceTab, trigger: HTMLButtonElement) => {
		externalEvidenceTriggerRef.current = trigger;
		setEvidenceTab(tab);
		onSourcesOpenChange(true);
	};

	return (
		<Box position="relative" pt={1} pb={4} borderRadius="lg" textAlign="left" bg="surface.canvas">
			{isLoading && !responseText && (
				<Box position="absolute" top={1}>
					<ChatResponseLoadingIndicator
						isLoading={isLoading}
						isChatMode={chatMode}
						statusLabel={statusLabel}
					/>
				</Box>
			)}
			<Box>
				<Box pt={4}>
					{responseText && pendingAction && onSubmitPendingReply && (
						<ChatPendingActionCard
							pendingAction={pendingAction}
							responseText={responseText}
							onSelectOption={onSubmitPendingReply}
						/>
					)}
					{responseText && !pendingAction && resolvedOutcome && offeredAction && (
						<ChatOfferOutcomeCard
							responseText={responseText}
							offeredAction={offeredAction}
							offerOutcome={resolvedOutcome}
						/>
					)}
					{responseText && !pendingAction && !resolvedOutcome && (
						<>
							<ChatResponseText
								responseText={responseText}
								parseResponseTextWithLinks={parseResponseTextWithLinks}
								sources={sources}
								citations={citations}
								documents={documents}
								isLoading={isLoading}
								showCitationPills={showCitationPills}
								onCitationClick={onCitationClick}
							/>
							{!offerOutcome && offeredAction && <ChatExpiredOfferNotice offeredAction={offeredAction} />}
						</>
					)}
					{!isLoading && responseText && exchangeId && (
						<ChatResponseActions
							exchangeId={exchangeId}
							responseText={responseText}
							sources={
								<ChatResponseSources
									sources={sources ?? []}
									webSources={webSources ?? []}
									appliedCorrections={appliedCorrections ?? []}
									documents={documents}
									highlightedAnnotationIndex={highlightedAnnotationIndex}
									registerAnnotationRef={registerAnnotationRef}
									onOpenDocument={onOpenDocument}
									open={sourcesOpen}
									onOpenChange={onSourcesOpenChange}
									activeTab={evidenceTab}
									onTabChange={setEvidenceTab}
									onInternalTriggerOpen={() => {
										externalEvidenceTriggerRef.current = null;
									}}
									finalFocusEl={() => externalEvidenceTriggerRef.current}
									onOpenAnimationStart={onSourcesAnimationStart}
								/>
							}
							webSources={
								<ChatResponseWebSources
									webSources={webSources ?? []}
									hideOnMobile
									onOpen={(trigger) => openEvidence('web', trigger)}
								/>
							}
							corrections={
								<ChatResponseCorrectionsBadge
									appliedCorrections={appliedCorrections ?? []}
									hideOnMobile
									onOpen={(trigger) => openEvidence('corrections', trigger)}
								/>
							}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default ChatContainerResponse;
